import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type { DrawingViewport, CreateDrawingViewport, UpdateDrawingViewport } from '@estimator/shared';

export function useDrawingViewports(projectId: string | null) {
  return useQuery({
    queryKey: ['drawing-viewports', projectId],
    queryFn: async () => {
      const { data } = await apiClient.get<DrawingViewport[]>(
        `/projects/${projectId}/drawing-viewports`,
      );
      return data;
    },
    enabled: !!projectId,
  });
}

export function useDrawingViewport(id: string | null) {
  return useQuery({
    queryKey: ['drawing-viewports', 'detail', id],
    queryFn: async () => {
      const { data } = await apiClient.get<DrawingViewport>(`/drawing-viewports/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export function useCreateDrawingViewport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, ...body }: CreateDrawingViewport & { projectId: string }) => {
      const { data } = await apiClient.post<DrawingViewport>(
        `/projects/${projectId}/drawing-viewports`,
        body,
      );
      return data;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['drawing-viewports', vars.projectId] });
    },
  });
}

export function useUpdateDrawingViewport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateDrawingViewport & { id: string }) => {
      const { data } = await apiClient.patch<DrawingViewport>(`/drawing-viewports/${id}`, body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drawing-viewports'] });
    },
  });
}

export function useDeleteDrawingViewport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/drawing-viewports/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drawing-viewports'] });
    },
  });
}
